/**
 * Arquivo de Configuração de Cores - White Label
 * 
 * Este arquivo centraliza todas as cores usadas no projeto.
 * Para personalizar para um novo cliente, basta alterar os valores aqui.
 * 
 * Estrutura:
 * - primary: Cores principais da marca
 * - secondary: Cores secundárias
 * - neutral: Tons neutros (cinzas, brancos, pretos)
 * - status: Cores de status (sucesso, erro, aviso, info)
 * - gradients: Gradientes utilizados
 * - shadows: Cores de sombras
 */

const colors = {
  // ============ CORES PRINCIPAIS DA MARCA ============
  primary: {
    main: '#8a1ce5',           // Roxo principal
    dark: '#4a00e0',           // Roxo escuro
    light: '#9b2ff7',          // Roxo claro
    lighter: '#c4a4e9',        // Roxo mais claro
    veryLight: '#b8a2e3',      // Roxo muito claro
    purple: '#611ea0',         // Roxo alternativo
    purpleDark: '#5402a3',     // Roxo escuro alternativo
    violet: '#a613fb',         // Violeta
  },

  // ============ CORES SECUNDÁRIAS ============
  secondary: {
    blue: '#0044cc',           // Azul principal
    blueLight: '#3498db',      // Azul claro
    blueDark: '#2980b9',       // Azul escuro
    blueDeep: '#1c49db',       // Azul profundo
    blueDarker: '#015',        // Azul muito escuro
    blueElectric: '#00f',      // Azul elétrico
    cyan: '#17a2b8',           // Ciano
    info: '#821CE5',           // Info (roxo-azulado)
  },

  // ============ TONS NEUTROS ============
  neutral: {
    black: '#000',             // Preto
    darkGray: '#2c3e50',       // Cinza escuro
    mediumDarkGray: '#34495e', // Cinza médio-escuro
    gray: '#666',              // Cinza médio
    gray2: '#6c757d',          // Cinza Bootstrap
    lightGray: '#888',         // Cinza claro
    veryLightGray: '#ddd',     // Cinza muito claro
    lighterGray: '#e0e0e0',   // Cinza mais claro
    superLightGray: '#eee',    // Cinza super claro
    offWhite: '#f0f0f0',       // Branco sujo
    white: '#fff',             // Branco puro
    white2: '#ffffff',         // Branco puro (alternativa)
    background: '#f5f7fa',     // Fundo claro
    backgroundGray: '#f8f9fa', // Fundo cinza claro
    backgroundLight: '#f9f9f9',// Fundo muito claro
    text: '#333',              // Texto padrão
    textDark: '#444',          // Texto escuro
  },

  // ============ CORES DE STATUS ============
  status: {
    success: '#28a745',        // Verde - Sucesso
    error: '#ff6b6b',          // Vermelho - Erro
    errorDark: '#ee0979',      // Vermelho escuro
    warning: '#ffc107',        // Amarelo - Aviso
    danger: '#dc3545',         // Vermelho - Perigo
  },

  // ============ GRADIENTES ============
  gradients: {
    // Gradientes Roxos
    purplePrimary: 'linear-gradient(135deg, #8a1ce5 0%, #4a00e0 100%)',
    purpleLight: 'linear-gradient(135deg, #9b2ff7 0%, #5a14f0 100%)',
    purpleSoft: 'linear-gradient(135deg, #c4a4e9 0%, #b8a2e3 100%)',
    purpleBlue: 'linear-gradient(45deg, #611ea0, #2980b9)',
    purpleBlueAlt: 'linear-gradient(45deg, #a613fb, #1c49db)',
    
    // Gradientes Azuis
    blueDeep: 'linear-gradient(27deg, #5402a3 50%, #015 100%)',
    blueElectric: 'linear-gradient(205deg, #821CE5 25%, #00f 138%)',
    
    // Gradientes de Erro
    errorGradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee0979 100%)',
    
    // Gradientes Transparentes
    whiteOverlay: 'linear-gradient(90deg, rgba(255, 255, 255, 0.2), transparent)',
    blackOverlay: 'linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent)',
  },

  // ============ SOMBRAS E OPACIDADES ============
  shadows: {
    // Sombras Pretas
    blackXs: 'rgba(0, 0, 0, 0.1)',
    blackSm: 'rgba(0, 0, 0, 0.15)',
    blackMd: 'rgba(0, 0, 0, 0.2)',
    blackLg: 'rgba(0, 0, 0, 0.6)',
    
    // Sombras Roxas
    purpleXs: 'rgba(74, 0, 224, 0.05)',
    purpleSm: 'rgba(74, 0, 224, 0.1)',
    purpleMd: 'rgba(74, 0, 224, 0.2)',
    purpleLg: 'rgba(74, 0, 224, 0.3)',
    purpleXl: 'rgba(74, 0, 224, 0.4)',
    purpleDeep: 'rgba(84, 11, 167, 0.2)',
    
    // Sombras de Erro
    errorShadow: 'rgba(238, 9, 121, 0.3)',
    
    // Sombras de Sucesso
    successBg: 'rgba(40, 167, 69, 0.1)',
    
    // Sombras Info
    infoBg: 'rgba(23, 162, 184, 0.1)',
    
    // Overlay branco
    whiteOverlay: 'rgba(255, 255, 255, 0.95)',
    whiteOverlayStrong: 'rgba(255, 255, 255, 0.98)',
  },

  // ============ CORES ESPECIAIS ============
  special: {
    border: '#ddd',
    borderLight: '#e0e0e0',
    borderDark: '#ccc',
    link: '#0044cc',
    linkHover: '#003399',
    inputBorder: '#ced4da',
    placeholder: '#6c757d',
  },
};

/**
 * Função auxiliar para obter uma cor
 * Uso: getColor('primary.main') retorna '#8a1ce5'
 */
export const getColor = (path) => {
  const keys = path.split('.');
  let value = colors;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Cor não encontrada: ${path}`);
      return '#000000';
    }
  }
  
  return value;
};

/**
 * Função para criar variáveis CSS customizadas
 * Retorna um objeto com variáveis CSS prontas para uso
 */
export const getCSSVariables = () => {
  const cssVars = {};
  
  // Primary colors
  Object.entries(colors.primary).forEach(([key, value]) => {
    cssVars[`--color-primary-${key}`] = value;
  });
  
  // Secondary colors
  Object.entries(colors.secondary).forEach(([key, value]) => {
    cssVars[`--color-secondary-${key}`] = value;
  });
  
  // Neutral colors
  Object.entries(colors.neutral).forEach(([key, value]) => {
    cssVars[`--color-neutral-${key}`] = value;
  });
  
  // Status colors
  Object.entries(colors.status).forEach(([key, value]) => {
    cssVars[`--color-status-${key}`] = value;
  });
  
  return cssVars;
};

export default colors;
